import React from "react";
import { css } from "@emotion/react";
import { useSelector } from "react-redux";
import { useFormContext } from "react-hook-form";
import { format, isAfter, isValid } from "date-fns";
import { useTranslation } from "react-i18next";
import { FullScreenModalDialog } from "main/javascripts/components/modal/FullScreenModalDialog";
import { isPhone } from "main/javascripts/styles/base/responsiveStyle";
import { DropdownOptionPicker } from "main/javascripts/components/optionPicker/DropdownOptionPicker";
import { FieldButton } from "main/javascripts/components/form/FieldButton";
import { useAppDispatch, useAppSelector } from "main/javascripts/store";
import { OptionPickerOption } from "main/javascripts/types/optionPickerOption";
import {
  cruiseSchedulesSelector,
  isCruiseSchedulePickerDisplayedSelector,
  setCruiseSchedulePickerDisplayed,
} from "../cruiseSlice";
import { parseDate } from "main/javascripts/utils/DateUtil";
import { ScrollableOptionPicker } from "main/javascripts/components/optionPicker/ScrollableOptionPicker";

export interface IProps {
  onUpdate?: any;
}

export const CruiseSchedulePickerBlock: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const { onUpdate } = props;

  const cruiseSchedules = useAppSelector(cruiseSchedulesSelector);
  const displayedPicker = useAppSelector(
    isCruiseSchedulePickerDisplayedSelector
  );
  // TODO: 旧reducerをそのまま使用
  const breakpoint: any = useSelector<any>(
    (state) => state.breakpointReducer.breakpoint
  );

  const {
    setValue,
    watch,
    register,
    formState: { errors },
  } = useFormContext();

  const appDispatch = useAppDispatch();

  const { t, i18n } = useTranslation(["label", "unit"]);

  const [departureDateValue] = watch(["departure_date"]);

  const formatDate = (dateString: string) => {
    const date = parseDate(dateString);
    if (isValid(date)) {
      return format(
        date,
        i18n.language === "ja" ? "yyyy/MM/dd" : "MMMM d, yyyy"
      );
    }
    return dateString;
  };

  const options = cruiseSchedules
    .filter((cruiseSchedule) => {
      const departureDate = parseDate(cruiseSchedule.departureDate);
      return isValid(departureDate) && isAfter(departureDate, new Date());
    })
    .map((cruiseSchedule) => {
      return {
        value: cruiseSchedule.departureDate,
        name: formatDate(cruiseSchedule.departureDate),
        description: t("unit:startsAt", { value: cruiseSchedule.price }),
      };
    });

  const onScheduleClick: () => void = () => {
    appDispatch(setCruiseSchedulePickerDisplayed(true));
  };

  const onPickerUpdate = (option: OptionPickerOption) => {
    setValue("departure_date", option.value);
    hidePicker();
    if (onUpdate) {
      onUpdate();
    }
  };

  const hidePicker: () => void = () => {
    appDispatch(setCruiseSchedulePickerDisplayed(false));
  };

  return (
    <div css={blockStyle}>
      <input type="hidden" {...register("departure_date")} />
      <div css={inputBlockStyle}>
        <FieldButton
          label={t("cruise.departureDate")}
          value={`${formatDate(departureDateValue)}`}
          onClick={onScheduleClick}
          isFocus={!!errors.checkin_date || !!errors.checkout_date}
          borderRadiusStyleKey="roundLeft"
          fontStyleKey="small"
        />
      </div>
      {isPhone(breakpoint) ? (
        <FullScreenModalDialog
          displayed={displayedPicker}
          closeHandler={hidePicker}
          colorMode="light"
        >
          <ScrollableOptionPicker
            options={options}
            onSelect={onPickerUpdate}
            hidePicker={hidePicker}
          />
        </FullScreenModalDialog>
      ) : (
        <div css={pickerBlockStyle}>
          <DropdownOptionPicker
            onSelect={onPickerUpdate}
            hidePicker={hidePicker}
            displayedPicker={displayedPicker}
            options={options}
          />
        </div>
      )}
    </div>
  );
};

const blockStyle = css`
  flex: 1;
`;
const inputBlockStyle = css`
  display: flex;
  padding: 0;
`;
const pickerBlockStyle = css`
  position: relative;
  min-width: 24rem;
`;
